import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoComplete, Button, Card, Col, Form, Input, Modal, Row, Select, Space, Typography, message } from 'antd';
import {
  FileTextOutlined,
  PlusOutlined,
  ProfileOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import AppBreadcrumb from '../../components/Breadcrumb';
import AppCard from '../../components/cards/AppCard';
import { addLmsFlow, addTestingOption, getActionFieldRules, getLmsFlows, getTestingOptions } from '../../services/testingApi';
import { zinnextActionOptions } from '../../services/dropdownValues/testPageValues';

const { Text } = Typography;

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'LMS Cases', href: '/lms-cases' },
];

const initialValues = {
  tags: 'regression',
  active: 1,
};

const testingOptionTypes = {
  module: {
    dbName: 'Modules',
    formField: 'module',
    label: 'Module',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CHANGE 1 — Action-based field validation matrix (same as ZinnextTestForm)
// true  = field is MANDATORY for this action
// false = field is NOT needed (will be disabled + placeholder updated)
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_FIELD_RULES = { key: false, value: false, expected: false };

function getFieldRules(actionFieldRules, action) {
  if (!action) return EMPTY_FIELD_RULES;
  return actionFieldRules[action] ?? EMPTY_FIELD_RULES;
}

function clearFieldErrors(form, fieldNames) {
  form.setFields(fieldNames.map((name) => ({ name, errors: [] })));
}
// ─────────────────────────────────────────────────────────────────────────────

function toSelectOptions(items = []) {
  return items.map((item) => ({
    value: item,
    label: item,
  }));
}

function normalizeText(value = '') {
  return String(value ?? '').trim();
}

function getUniqueFlowOptions(rows = []) {
  const flowMap = new Map();

  rows.forEach((row) => {
    const flow = normalizeText(row?.flow);
    const key = flow.toLowerCase();

    if (flow && !flowMap.has(key)) {
      flowMap.set(key, flow);
    }
  });

  return Array.from(flowMap.values()).map((flow) => ({
    value: flow,
    label: flow,
  }));
}

function getNextStepOrder(rows = [], module, flow) {
  const selectedModule = normalizeText(module).toLowerCase();
  const selectedFlow = normalizeText(flow).toLowerCase();
  const lastStepOrder = rows.reduce((maxStepOrder, row) => {
    const rowModule = normalizeText(row?.module).toLowerCase();
    const rowFlow = normalizeText(row?.flow).toLowerCase();

    if (rowModule !== selectedModule || rowFlow !== selectedFlow) {
      return maxStepOrder;
    }

    const stepOrder = Number(row?.step_order) || 0;
    return Math.max(maxStepOrder, stepOrder);
  }, 0);

  return lastStepOrder + 1;
}

function SectionTitle({ icon, title }) {
  return (
    <Space size={10} align="center">
      <div className="v1-section-icon-box">{icon}</div>
      <Text strong className="v1-section-label">{title}</Text>
    </Space>
  );
}

export default function LmsTestForm() {
  const [form] = Form.useForm();
  const [optionForm] = Form.useForm();
  const [moduleItems, setModuleItems] = useState([]);
  const [flowRows, setFlowRows] = useState([]);
  const [flowOptions, setFlowOptions] = useState([]);
  const [actionOptions, setActionOptions] = useState(zinnextActionOptions);
  const [actionFieldRules, setActionFieldRules] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [optionModal, setOptionModal] = useState({ open: false, typeKey: 'module' });
  const [optionSaving, setOptionSaving] = useState(false);
  const [stepOrderLoading, setStepOrderLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const selectedOptionType = testingOptionTypes[optionModal.typeKey];
  const moduleOptions = useMemo(() => toSelectOptions(moduleItems), [moduleItems]);

  const selectedModule   = Form.useWatch('module',  form);
  const selectedFlow     = Form.useWatch('flow',    form);
  // CHANGE 2 — watch the action field so validation matrix can react
  const selectedAction   = Form.useWatch('keyword', form);

  const hasSelectedModule = Boolean(normalizeText(selectedModule));
  const hasSelectedFlow   = Boolean(normalizeText(selectedFlow));
  const areFlowFieldsDisabled = !hasSelectedModule;
  const areDetailsDisabled    = !hasSelectedModule || !hasSelectedFlow;

  // CHANGE 3 — derive live rules from currently selected action
  const fieldRules = useMemo(() => getFieldRules(actionFieldRules, selectedAction), [actionFieldRules, selectedAction]);

  const setItemsByType = (typeKey, items = []) => {
    if (typeKey === 'module') {
      setModuleItems(items);
    }
  };

  const loadTestingOptions = async () => {
    setOptionsLoading(true);

    try {
      const modulesResponse = await getTestingOptions(testingOptionTypes.module.dbName);
      setModuleItems(modulesResponse?.items ?? []);
    } catch (error) {
      console.error('Unable to load testing options:', error);
      message.error('Unable to load testing options.');
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadActionFieldRules = async () => {
    try {
      const res = await getActionFieldRules();
      if (Array.isArray(res?.options) && res.options.length > 0) {
        setActionOptions(res.options);
      }
      if (res?.rules && typeof res.rules === 'object') {
        setActionFieldRules(res.rules);
      }
    } catch (error) {
      console.error('Unable to load action field rules:', error);
      message.error('Unable to load action validation rules.');
    }
  };

  const loadModuleFlows = useCallback(async (module) => {
    const normalizedModule = normalizeText(module);

    setFlowRows([]);
    setFlowOptions([]);
    form.setFieldsValue({ step_order: undefined });

    if (!normalizedModule) return;

    setFlowsLoading(true);

    try {
      const response = await getLmsFlows({ limit: 10000, offset: 0, module: normalizedModule });
      const rows = Array.isArray(response?.items) ? response.items : [];

      setFlowRows(rows);
      setFlowOptions(getUniqueFlowOptions(rows));
    } catch (error) {
      console.error('Unable to load flows:', error);
      message.error('Unable to load flows.');
    } finally {
      setFlowsLoading(false);
    }
  }, [form]);

  const loadNextStepOrder = useCallback(async (module, flow, rows) => {
    const normalizedModule = normalizeText(module);
    const normalizedFlow   = normalizeText(flow);

    if (!normalizedModule || !normalizedFlow) {
      form.setFieldsValue({ step_order: undefined });
      return;
    }

    setStepOrderLoading(true);

    try {
      form.setFieldsValue({ step_order: getNextStepOrder(rows, normalizedModule, normalizedFlow) });
    } catch (error) {
      console.error('Unable to load step order:', error);
      message.error('Unable to load step order.');
    } finally {
      setStepOrderLoading(false);
    }
  }, [form]);

  useEffect(() => {
    Promise.resolve().then(async () => {
      await Promise.all([loadTestingOptions(), loadActionFieldRules()]);
    });
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => loadModuleFlows(selectedModule));
  }, [loadModuleFlows, selectedModule]);

  useEffect(() => {
    Promise.resolve().then(() => loadNextStepOrder(selectedModule, selectedFlow, flowRows));
  }, [flowRows, loadNextStepOrder, selectedFlow, selectedModule]);

  const onFinish = async (values) => {
    const payload = {
      module:      normalizeText(values.module),
      flow:        normalizeText(values.flow),
      step_order:  Number(values.step_order),
      description: normalizeText(values.description),
      keyword:     normalizeText(values.keyword),
      target:      normalizeText(values.target),
      value:       normalizeText(values.value),
      expected:    normalizeText(values.expected),
      tags:        values.tags ? normalizeText(values.tags) : initialValues.tags,
      active:      Number(values.active),
    };

    setSubmitting(true);
    try {
      await addLmsFlow(payload);
      message.success('LMS case details saved successfully.');
      form.resetFields();
      setFlowRows([]);
      setFlowOptions([]);
    } catch (error) {
      console.error('Unable to save LMS case:', error);
      message.error('Unable to save LMS case.');
    } finally {
      setSubmitting(false);
    }
  };

  const removeExtraSpaces = (fieldName, value) => {
    form.setFieldsValue({ [fieldName]: normalizeText(value) });
  };

  const handleModuleChange = (value) => {
    form.setFieldsValue({
      module:      normalizeText(value),
      flow:        undefined,
      step_order:  undefined,
      keyword:     undefined,
      description: undefined,
      target:      undefined,
      tags:        initialValues.tags,
      value:       undefined,
      expected:    undefined,
    });
    clearFieldErrors(form, ['keyword', 'target', 'value', 'expected']);
  };

  const handleFlowChange = (value) => {
    form.setFieldsValue({
      flow: normalizeText(value),
      step_order: undefined,
      keyword: undefined,
      target: undefined,
      value: undefined,
      expected: undefined,
    });
    clearFieldErrors(form, ['keyword', 'target', 'value', 'expected']);
  };

  // CHANGE 4 — when action changes, clear fields not needed for the new action
  const handleActionChange = (value) => {
    const rules = getFieldRules(actionFieldRules, value);
    form.setFieldsValue({
      keyword:  normalizeText(value),
      target:   rules.key      ? form.getFieldValue('target')   : undefined,
      value:    rules.value    ? form.getFieldValue('value')    : undefined,
      expected: rules.expected ? form.getFieldValue('expected') : undefined,
    });
    clearFieldErrors(form, ['target', 'value', 'expected']);
  };

  const openOptionModal = (typeKey) => {
    const optionType = testingOptionTypes[typeKey];
    setOptionModal({ open: true, typeKey });
    optionForm.setFieldsValue({ name: '', type: optionType.label });
  };

  const closeOptionModal = () => {
    setOptionModal((current) => ({ ...current, open: false }));
    optionForm.resetFields();
  };

  const saveTestingOption = async () => {
    try {
      const values  = await optionForm.validateFields();
      const name    = normalizeText(values.name);
      const response = await addTestingOption({ name, type: selectedOptionType.dbName });

      setItemsByType(optionModal.typeKey, response?.items ?? []);
      form.setFieldsValue({ [selectedOptionType.formField]: name });
      message.success(`${selectedOptionType.label} added successfully.`);
      closeOptionModal();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Unable to save testing option:', error);
      message.error(`Unable to add ${selectedOptionType.label.toLowerCase()}.`);
    } finally {
      setOptionSaving(false);
    }
  };

  const submitTestingOption = () => {
    setOptionSaving(true);
    saveTestingOption();
  };

  return (
    <main className="jobs-list-page">
      <Row align="middle" justify="space-between" className="breadcrumb-action-wrapper">
        <Col>
          <AppBreadcrumb items={breadcrumbItems} />
        </Col>
        <Col>
          <Button type="link" onClick={() => navigate('/lms-home')}>
            View Table
          </Button>
        </Col>
      </Row>

      <div className="v1-page">
        <div className="v1-page-inner">
          <div className="v1-page-header">
            <Space align="center" size={10}>
              <ProfileOutlined className="v1-outer-icon" />
              <div>
                <Text strong style={{ fontSize: 16 }}>Add LMS Case</Text>
              </div>
            </Space>
          </div>

          <AppCard>
            <Form
              form={form}
              className="v1-form"
              layout="vertical"
              colon={false}
              requiredMark={(label, { required }) => (
                <>
                  {label}
                  {required && <span className="v1-required-mark"> *</span>}
                </>
              )}
              initialValues={initialValues}
              onFinish={onFinish}
              autoComplete="off"
            >
              <Card
                className="v1-card"
                title={<SectionTitle icon={<FileTextOutlined />} title="LMS Case Details" />}
                extra={
                  <Text type="secondary" className="v1-card-subtitle">
                    Module, flow, step order and execution data
                  </Text>
                }
              >
                <Row gutter={[24, 0]}>
                  {/* Module */}
                  <Col xs={24} md={12}>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item label="Module" name="module" style={{ flex: 1 }}>
                        <Select
                          allowClear
                          showSearch
                          loading={optionsLoading}
                          optionFilterProp="label"
                          placeholder="Select module"
                          options={moduleOptions}
                          onChange={handleModuleChange}
                        />
                      </Form.Item>
                      <Button
                        aria-label="Add module"
                        icon={<PlusOutlined />}
                        style={{ marginTop: 30 }}
                        onClick={() => openOptionModal('module')}
                      />
                    </Space.Compact>
                  </Col>

                  {/* Flow */}
                  <Col xs={24} md={12}>
                    <Form.Item label="Flow" name="flow">
                      <AutoComplete
                        maxLength={100}
                        allowClear
                        disabled={areFlowFieldsDisabled}
                        options={flowOptions}
                        placeholder={flowsLoading ? 'Loading flows' : 'Enter flow'}
                        onChange={handleFlowChange}
                        filterOption={(inputValue, option) =>
                          option?.value?.toLowerCase().includes(inputValue.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>

                  {/* Step Order */}
                  <Col xs={24} md={12}>
                    <Form.Item label="Step Order" name="step_order" validateTrigger={['onBlur', 'onChange']}>
                      <Input
                        maxLength={10}
                        disabled
                        placeholder={stepOrderLoading ? 'Loading step order' : 'Step order'}
                      />
                    </Form.Item>
                  </Col>

                  {/* Action — unchanged, just wired to handleActionChange */}
                  <Col xs={24} md={12}>
                    <Form.Item label="Action" name="keyword">
                      <Select
                        allowClear
                        showSearch
                        disabled={areDetailsDisabled}
                        optionFilterProp="label"
                        placeholder="Select action"
                        options={actionOptions}
                        onChange={handleActionChange}
                      />
                    </Form.Item>
                  </Col>

                  {/* Description — not in validation matrix, always editable */}
                  <Col xs={24}>
                    <Form.Item label="Description" name="description">
                      <Input.TextArea
                        rows={3}
                        maxLength={255}
                        disabled={areDetailsDisabled}
                        placeholder="Enter description"
                        showCount
                        onBlur={(e) => removeExtraSpaces('description', e.target.value)}
                      />
                    </Form.Item>
                  </Col>

                  {/* ─────────────────────────────────────────────────────────
                      CHANGE 5 — Key (target), Value, Expected now have
                      dynamic required rules + disabled + placeholder
                      based on the selected action keyword.
                      ───────────────────────────────────────────────────────── */}

                  {/* Key (stored as "target" in this form) */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Key"
                      name="target"
                      rules={[
                        {
                          required: fieldRules.key,
                          message: 'Key is required for this action',
                        },
                      ]}
                    >
                      <Input
                        maxLength={255}
                        disabled={areDetailsDisabled || (Boolean(selectedAction) && !fieldRules.key)}
                        placeholder={
                          areDetailsDisabled
                            ? 'Select module and flow first'
                            : !selectedAction
                            ? 'Select an action first'
                            : fieldRules.key
                            ? 'Required — Enter selector / locator'
                            : 'Not needed for this action'
                        }
                        onBlur={(e) => removeExtraSpaces('target', e.target.value)}
                      />
                    </Form.Item>
                  </Col>

                  {/* Tags — not in validation matrix, always editable when details enabled */}
                  <Col xs={24} md={12}>
                    <Form.Item label="Tags" name="tags">
                      <Input
                        maxLength={200}
                        disabled={areDetailsDisabled}
                        placeholder="Enter tags"
                        prefix={<TagsOutlined className="v1-input-icon" />}
                        onBlur={(e) => removeExtraSpaces('tags', e.target.value)}
                      />
                    </Form.Item>
                  </Col>

                  {/* Value */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Value"
                      name="value"
                      rules={[
                        {
                          required: fieldRules.value,
                          message: 'Value is required for this action',
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        disabled={areDetailsDisabled || (Boolean(selectedAction) && !fieldRules.value)}
                        placeholder={
                          areDetailsDisabled
                            ? 'Select module and flow first'
                            : !selectedAction
                            ? 'Select an action first'
                            : fieldRules.value
                            ? 'Required — Enter value'
                            : 'Not needed for this action'
                        }
                        onBlur={(e) => removeExtraSpaces('value', e.target.value)}
                      />
                    </Form.Item>
                  </Col>

                  {/* Expected */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Expected"
                      name="expected"
                      rules={[
                        {
                          required: fieldRules.expected,
                          message: 'Expected result is required for this action',
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        disabled={areDetailsDisabled || (Boolean(selectedAction) && !fieldRules.expected)}
                        placeholder={
                          areDetailsDisabled
                            ? 'Select module and flow first'
                            : !selectedAction
                            ? 'Select an action first'
                            : fieldRules.expected
                            ? 'Required — Enter expected result'
                            : 'Not needed for this action'
                        }
                        onBlur={(e) => removeExtraSpaces('expected', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  {/* ───────────────────────────────────────────────────────── */}
                </Row>
              </Card>

              <div className="v1-form-actions">
                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      disabled={areDetailsDisabled}
                    >
                      Submit
                    </Button>
                    <Button
                      onClick={() => {
                        form.resetFields();
                        setFlowRows([]);
                        setFlowOptions([]);
                      }}
                    >
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </div>
            </Form>
          </AppCard>

          <Modal
            title={`Add ${selectedOptionType.label}`}
            open={optionModal.open}
            confirmLoading={optionSaving}
            okText="Add"
            onCancel={closeOptionModal}
            onOk={submitTestingOption}
            destroyOnHidden
          >
            <Form
              form={optionForm}
              layout="vertical"
              colon={false}
              className="v1-form"
              requiredMark={(label, { required }) => (
                <>
                  {label}
                  {required && <span className="v1-required-mark"> *</span>}
                </>
              )}
            >
              <Form.Item label="Name" name="name">
                <Input
                  maxLength={100}
                  placeholder={`Enter ${selectedOptionType.label.toLowerCase()} name`}
                  onBlur={(e) => {
                    optionForm.setFieldsValue({ name: normalizeText(e.target.value) });
                  }}
                />
              </Form.Item>
              <Form.Item label="Type" name="type">
                <Input disabled />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>
    </main>
  );
}
